import {
    buildPrintReportModalTOCItems,
    PrintReportModalTOCItem,
    TaxReturnTOCItemWithRequiredNodeKey,
    traversePrintReportModalTOCItem,
} from '../data-types/printReportModalTOCItem';
import { TaxReturnTOC } from '../model';

export const buildTocItemsFromTOC = (formTOCData: TaxReturnTOC | null | undefined): PrintReportModalTOCItem[] => {
    if (!formTOCData) {
        return [];
    }
    return buildPrintReportModalTOCItems(formTOCData.items ?? []);
};

export const getInitialSelectedNodeKeys = (
    tocItems: PrintReportModalTOCItem[],
    initialSelectedTocItemsOverride?: TaxReturnTOCItemWithRequiredNodeKey[]
): Set<string> => {
    if (initialSelectedTocItemsOverride?.length) {
        const overrideNodeKeys: string[] = initialSelectedTocItemsOverride.map(
            (item: TaxReturnTOCItemWithRequiredNodeKey): string => item.nodeKey
        );
        return new Set<string>(overrideNodeKeys);
    }

    const initialSelectedNodeKeys: Set<string> = new Set<string>();

    tocItems.forEach((tocItem: PrintReportModalTOCItem): void => {
        traversePrintReportModalTOCItem(tocItem, (currentItem: PrintReportModalTOCItem): void => {
            const hasChildren: boolean = (currentItem.children?.length ?? 0) > 0;
            if (hasChildren) {
                return;
            }

            if (currentItem.isSelectedForPrint) {
                initialSelectedNodeKeys.add(currentItem.nodeKey);
            }
        });
    });

    return initialSelectedNodeKeys;
};

const buildParentNodeKeyByNodeKey = (tocItems: PrintReportModalTOCItem[]): Map<string, string | null> => {
    const parentNodeKeyByNodeKey: Map<string, string | null> = new Map<string, string | null>();

    const addParentNodeKeys = (items: PrintReportModalTOCItem[], parentNodeKey: string | null): void => {
        items.forEach((item: PrintReportModalTOCItem): void => {
            parentNodeKeyByNodeKey.set(item.nodeKey, parentNodeKey);

            const childItems: PrintReportModalTOCItem[] = item.children ?? [];
            addParentNodeKeys(childItems, item.nodeKey);
        });
    };

    addParentNodeKeys(tocItems, null);
    return parentNodeKeyByNodeKey;
};

export const getInitiallyExpandedNodeKeys = (tocItems: PrintReportModalTOCItem[], selectedLeafNodeKeys: Set<string>): Set<string> => {
    const initialExpandedNodeKeys: Set<string> = new Set<string>();
    const parentNodeKeyByNodeKey: Map<string, string | null> = buildParentNodeKeyByNodeKey(tocItems);

    tocItems.forEach((tocItem: PrintReportModalTOCItem): void => {
        traversePrintReportModalTOCItem(tocItem, (currentItem: PrintReportModalTOCItem): void => {
            const hasChildren: boolean = (currentItem.children?.length ?? 0) > 0;

            if (hasChildren && currentItem.isExpanded) {
                initialExpandedNodeKeys.add(currentItem.nodeKey);
            }
        });
    });

    selectedLeafNodeKeys.forEach((selectedLeafNodeKey: string): void => {
        let ancestorNodeKey: string | null = parentNodeKeyByNodeKey.get(selectedLeafNodeKey) ?? null;

        while (ancestorNodeKey) {
            initialExpandedNodeKeys.add(ancestorNodeKey);
            ancestorNodeKey = parentNodeKeyByNodeKey.get(ancestorNodeKey) ?? null;
        }
    });

    return initialExpandedNodeKeys;
};
